
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { KolPic } from '@/types/table';
import { Plus, User, Shield, Building2, Mail, Trash2, Eye, EyeOff } from 'lucide-react';
import { ModifyAccessDialog } from './ModifyAccessDialog';
import { ModifyPasswordDialog } from './ModifyPasswordDialog';
import { ModifyBuDialog } from './ModifyBuDialog';

interface UserManagementProps {
  users: KolPic[];
  isLoading: boolean;
  onRefresh: () => Promise<void>;
}

interface NewUserForm {
  name: string;
  email: string;
  password: string;
  access: 'full-access' | 'limited' | 'bu-access';
}

export function UserManagement({ users, isLoading, onRefresh }: UserManagementProps) {
  const [newUser, setNewUser] = useState<NewUserForm>({
    name: '',
    email: '',
    password: '',
    access: 'limited'
  });
  const [isCreating, setIsCreating] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showPasswords, setShowPasswords] = useState<Record<string, boolean>>({});
  const { toast } = useToast();

  // Helper function to safely parse JSONB BU data
  const parseBuData = (buData: any): string[] => {
    if (!buData) return [];
    if (Array.isArray(buData)) return buData.map(item => String(item));
    if (typeof buData === 'string') {
      try {
        const parsed = JSON.parse(buData);
        if (Array.isArray(parsed)) return parsed.map(item => String(item));
        return [buData];
      } catch (e) {
        return [buData];
      }
    }
    return [];
  };

  // Function to notify users of changes via localStorage timestamp
  const notifyUserChanges = () => {
    // Set a timestamp that other tabs/sessions can check
    localStorage.setItem('dashboard_user_changes_timestamp', Date.now().toString());
  };

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!newUser.name.trim() || !newUser.email.trim() || !newUser.password.trim()) {
      toast({
        title: 'Validation Error',
        description: 'All fields are required',
        variant: 'destructive',
      });
      return;
    }

    setIsCreating(true);
    try {
      // Get the current user's session to authenticate the request
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError || !session) {
        throw new Error('Not authenticated. Please log in again.');
      }

      // Call the create-user edge function
      const { data, error } = await supabase.functions.invoke('create-user', {
        body: {
          name: newUser.name.trim(),
          email: newUser.email.trim(),
          password: newUser.password.trim(),
          access: newUser.access,
        },
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (error) {
        console.error('Edge function error:', error);
        throw new Error(error.message || 'Failed to create user');
      }

      if (!data.success) {
        throw new Error(data.error || 'User creation failed');
      }

      toast({
        title: 'Success',
        description: `User ${newUser.name} created successfully`,
      });

      setNewUser({
        name: '',
        email: '',
        password: '',
        access: 'limited'
      });
      setShowCreateDialog(false);
      notifyUserChanges();
      await onRefresh();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to create user',
        variant: 'destructive',
      });
    } finally {
      setIsCreating(false);
    }
  };

  const handleDeleteUser = async (user: KolPic) => {
    if (!confirm(`Delete user "${user.name}"? This action cannot be undone.`)) {
      return;
    }

    try {
      const { error } = await supabase
        .from('kol_pic')
        .delete()
        .eq('auth_id', user.auth_id);

      if (error) throw error;

      toast({
        title: 'Success',
        description: `User ${user.name} deleted successfully`,
      });

      notifyUserChanges();
      await onRefresh();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to delete user',
        variant: 'destructive',
      });
    }
  };

  const togglePasswordVisibility = (userId: string) => {
    setShowPasswords(prev => ({
      ...prev,
      [userId]: !prev[userId]
    }));
  };

  const getAccessColor = (access: string) => {
    switch (access) {
      case 'full-access':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      case 'bu-access':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      case 'limited':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
    }
  };

  // Enhanced callback that includes change notification
  const handleUserDataChange = async () => {
    notifyUserChanges();
    await onRefresh();
  };

  if (isLoading) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
        <p className="text-muted-foreground mt-2">Loading users...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Create User Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogTrigger asChild>
          <Button>
            <Plus className="h-4 w-4 mr-2" />
            Create New User
          </Button>
        </DialogTrigger>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Create New User</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateUser} className="space-y-4">
            <div>
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                value={newUser.name}
                onChange={(e) => setNewUser(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Enter user name"
                required
              />
            </div>
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={newUser.email}
                onChange={(e) => setNewUser(prev => ({ ...prev, email: e.target.value }))}
                placeholder="Enter email address"
                required
              />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                value={newUser.password}
                onChange={(e) => setNewUser(prev => ({ ...prev, password: e.target.value }))}
                placeholder="Enter password"
                required
              />
            </div>
            <div>
              <Label htmlFor="access">Access Level</Label>
              <select
                id="access"
                value={newUser.access}
                onChange={(e) => setNewUser(prev => ({ ...prev, access: e.target.value as any }))}
                className="w-full p-2 border border-input rounded-md bg-background text-sm"
              >
                <option value="limited">Limited</option>
                <option value="bu-access">BU Access</option>
                <option value="full-access">Full Access</option>
              </select>
            </div>
            <div className="flex gap-2 pt-4">
              <Button type="submit" disabled={isCreating} className="flex-1">
                {isCreating ? 'Creating...' : 'Create User'}
              </Button>
              <Button type="button" variant="outline" onClick={() => setShowCreateDialog(false)}>
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Users Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
        {users.map((user) => {
          const userBUs = parseBuData(user.bu);
          
          return (
            <Card key={user.id} className="hover:shadow-md transition-shadow">
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <User className="h-4 w-4 text-primary" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <h3 className="font-semibold text-sm truncate">{user.name}</h3>
                      <div className="flex items-center gap-1 mt-1">
                        <Mail className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                        <span className="text-xs text-muted-foreground truncate">{user.email}</span>
                      </div>
                    </div>
                  </div>
                  <Badge className={`${getAccessColor(user.access || 'limited')} text-xs flex-shrink-0`}>
                    <Shield className="h-3 w-3 mr-1" />
                    {user.access === 'full-access' ? 'Full' : user.access === 'bu-access' ? 'BU' : 'Limited'}
                  </Badge>
                </div>
              </CardHeader>
              
              <CardContent className="pt-0 space-y-3">
                {/* Business Units */}
                <div>
                  <div className="flex items-center gap-1 mb-1">
                    <Building2 className="h-3 w-3 text-muted-foreground" />
                    <span className="text-xs font-medium text-muted-foreground">
                      BU ({userBUs.length})
                    </span>
                  </div>
                  {userBUs.length > 0 ? (
                    <div className="flex flex-wrap gap-1">
                      {userBUs.slice(0, 3).map((bu, index) => (
                        <Badge key={`${bu}-${index}`} variant="outline" className="text-xs">
                          {bu}
                        </Badge>
                      ))}
                      {userBUs.length > 3 && (
                        <Badge variant="outline" className="text-xs">
                          +{userBUs.length - 3} more
                        </Badge>
                      )}
                    </div>
                  ) : (
                    <p className="text-xs text-muted-foreground italic">No BU assigned</p>
                  )}
                </div>

                {/* Password */}
                {user.password && (
                  <div>
                    <div className="flex items-center gap-1 mb-1">
                      <span className="text-xs font-medium text-muted-foreground">Password</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <code className="text-xs bg-muted px-2 py-1 rounded flex-1 truncate">
                        {showPasswords[user.id] ? user.password : '••••••••'}
                      </code>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => togglePasswordVisibility(user.id)}
                        className="h-6 w-6 p-0 flex-shrink-0"
                      >
                        {showPasswords[user.id] ? (
                          <EyeOff className="h-3 w-3" />
                        ) : (
                          <Eye className="h-3 w-3" />
                        )}
                      </Button>
                    </div>
                  </div>
                )}

                {/* Action buttons */}
                <div className="flex gap-1 pt-2">
                  <ModifyAccessDialog user={user} onAccessChanged={handleUserDataChange} />
                  <ModifyPasswordDialog user={user} onPasswordChanged={handleUserDataChange} />
                  <ModifyBuDialog user={user} onBuChanged={handleUserDataChange} />
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => handleDeleteUser(user)}
                    className="h-8 w-20 text-xs"
                  >
                    <Trash2 className="h-3 w-3 mr-1" />
                    Delete
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {users.length === 0 && (
        <div className="text-center py-8">
          <User className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">No users found</p>
        </div>
      )}
    </div>
  );
}
